<template>
    <!-- Box styling (padding, border) comes from the ui-header-button class
         passed by theme headers; the dropdown anchors to this same element. -->
    <div v-on-click-outside="hide" class="relative leading-none select-none" @click="toggle">
        <span class="sr-only">View notifications</span>
        <notification-badge />
        <icon
            icon="hero/outline/bell"
            class="w-5 h-5 cursor-pointer"
            :class="[hasUnread ? 'text-gray-600 dark:text-zinc-50' : 'text-gray-600 dark:text-zinc-300']"
        ></icon>
        <div
            v-show="expanded"
            class="absolute right-0 py-2 mt-2 text-sm bg-white top-full w-[220px] space-y-4 text-black dark:text-black max-h-[320px] overflow-y-auto z-20 dark:bg-zinc-800 rounded-md shadow-lg ring-1 ring-black/5 overflow-x-hidden"
        >
            <div v-if="hasMessages">
                <notification-message v-for="message in messages" :key="message.id" :message="message" />
            </div>
            <div v-else>
                <IllustratedMessage>
                    <template #image>
                        <icon icon="hero/outline/bell-slash" class="w-12 h-12"></icon>
                    </template>
                    <template #title>{{ lang('notifications::notifications.no_entries') }} </template>
                    {{ lang('notifications::notifications.no_entries_description') }}
                </IllustratedMessage>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import axios from 'axios';
import { ref, onMounted, computed, watch } from 'vue';
import { vOnClickOutside } from '@vueuse/components';
import { useFavicon } from '@vueuse/core';
import { usePage } from '@inertiajs/vue3';
import { useNotificationArea } from '@src/Notifications/Pages/composables/useNotificationArea';
import NotificationMessage from '@src/Notifications/Pages/NotificationMessage.vue';
import NotificationBadge from '@src/Notifications/Pages/NotificationBadge.vue';

const messages = ref([]);

const expanded = ref(false);

const unreadChangeIndicate = ref(false);

const { delayedMarkUnreadAsRead } = useNotificationArea();

const loadNotifications = async () => {
    try {
        const { data } = await axios.get(route('notifications.index'));
        messages.value = data?.notifications || [];
    } catch (error) {
        console.error('Failed to load notifications:', error);
        messages.value = [];
    }
};

onMounted((): void => {
    loadNotifications();
});

const isExpanded = computed((): boolean => expanded.value);

const expand = (): void => {
    expanded.value = true;
};

const hide = (): void => {
    expanded.value = false;
};

const toggle = (): void => {
    if (isExpanded.value) {
        hide();
    } else {
        expand();
    }
};

const unreadMessages = computed(() => {
    return Array.isArray(messages.value) ? messages.value.filter((item) => item.unread) : [];
});

const unreadCount = computed(() => {
    return unreadMessages.value.length;
});

const hasUnread = computed(() => {
    return unreadCount.value > 0;
});

const hasMessages = computed(() => {
    return Array.isArray(messages.value) && messages.value.length > 0;
});

watch(
    isExpanded,
    (value) => {
        if (value) {
            delayedMarkUnreadAsRead();
        }
    },
    { immediate: true },
);

watch(unreadCount, (value) => {
    unreadChangeIndicate.value = true;
    setTimeout(() => {
        unreadChangeIndicate.value = false;
    }, 500);

    // Set favicon with notifications or standard according to unread count
    if (value > 0) {
        faviconType.value = 'notifications';
    } else {
        faviconType.value = 'standard';
    }
});

// Subscriber for notifications
if (window.Echo) {
    window.Echo.channel('admin').listen('.inquiry.created', () => {
        loadNotifications();
    });

    window.Echo.channel('admin').listen('.order.created', () => {
        loadNotifications();
    });

    window.Echo.channel('admin').listen('.notification', () => {
        loadNotifications();
    });
}

/**
 * Favicon
 */
const faviconType = ref('standard');

const page = usePage();
const environment = (page.props as any)?.app?.env || 'local';
const theme = (page.props as any)?.theme?.theme || 'default';

const faviconEnvironment = computed(() => (environment === 'production' ? 'production' : 'development'));

const faviconUrl = computed(() =>
    faviconType.value === 'standard'
        ? `themes/${theme}/images/${faviconEnvironment.value}/favicon-32x32.png`
        : `themes/${theme}/images/${faviconEnvironment.value}/notifications/favicon-32x32.png`,
);

useFavicon(faviconUrl, {
    baseUrl: '/',
    rel: 'icon',
});
</script>
